import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { MapHeaderControlsService } from '@app/energy-map/components-new/map-header-controls/map-header-controls.service';
import { EmailScenarioDialogComponent } from '@app/energy-map/dialog/email-scenario-dialog/email-scenario-dialog.component';
import { SaveScenarioDialogComponent } from '@app/energy-map/dialog/save-scenario-dialog/save-scenario-dialog.component';
import { StreetlightEfficiencyDialogComponent } from '@app/energy-map/dialog/streetlight-efficiency-dialog/streetlight-efficiency-dialog.component';
import { EnergyMapService } from '@app/energy-map/services/energy-map-service';
import { SidebarComponent } from '@app/shared/components/sidebar/sidebar.component';
import { EmailContents } from '@app/shared/constants';
import { NotificationDialogComponent } from '@app/shared/dialog/notification-dialog/notification-dialog.component';
import { ApplicationInsightEvent, Roles } from '@app/shared/enums';
import { DescriptiveNotificationComponent } from '@app/shared/notifications/descriptive-notification/descriptive-notification.component';
import { NotificationsService } from '@app/shared/notifications/notifications.service';
import { LocalNumberPipe } from '@app/shared/pipes/local-number.pipe';
import { Email, EmailService } from '@app/shared/services/api/email.service';
import { MunicipalityService } from '@app/shared/services/api/municipality.service';
import { StreetlightApiService } from '@app/shared/services/api/streetlight.service';
import { AppInsightsServiceDirective } from '@app/shared/services/app-insights.service';
import { AuthService } from '@app/shared/services/auth.service';
import { KamService } from '@app/shared/services/kam.service';
import { OverlayLoaderService } from '@app/shared/services/overlay-loader.service';
import { SidebarService } from '@app/shared/services/sidebar.service';
import { UserMunicipalitiesService } from '@app/shared/services/user-municipalities.service';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';
import {
  capitalize,
  chain,
  cloneDeep,
  differenceBy,
  differenceWith,
  find,
  forEach,
  get,
  intersectionBy,
  isEmpty,
  isEqual,
  isNil,
  map,
  pull,
  pullAllBy,
  some,
  sumBy,
  trim,
  zipObject,
} from 'lodash';
import { Subject, Subscription, forkJoin, of } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { ScenarioPlannerService } from '../../../shared/services/api/scenario-planner.service';
import { LampOptions } from '../../interfaces/LampOptions';
import { LampsSelectedService } from '../../services/lamps-selected.service';
import { MapStoreService } from '../../services/map-store.service';
import { MapService } from '../../services/map.service';
import { Luminaire } from '@app/shared/models/energiekarte/luminaire';
import { User } from '@app/shared/models/user';

@Component({
  selector: 'app-scenario-panel',
  templateUrl: './scenario-panel.component.html',
  styleUrls: ['./scenario-panel.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ScenarioPanelComponent extends SidebarComponent implements OnInit, OnDestroy {
  protected unsubscribe$: Subject<void> = new Subject<void>();
  public lightCategory: any[] = [];
  public productCatalog: boolean = false;
  public productDetail: boolean = false;
  public previouslySavedLamps: any[] = [];
  public selectedLampGroup: any;
  public selectedLampGroupCount: number = 0;
  public selectedLampGroupType: string = '';
  public selectedLampIds: any[] = [];
  public isLegendShown = false;
  public lamp: any;
  public lampsCount: number = 0;
  public selectedLampId: any;
  public energySavings = 0;
  public costSavings = 0;
  public cO2Savings = 0;
  public uvpCost = 0;
  public salesCost = 0;
  public isKam: boolean;
  public isAbleToSendToAnyEmail = false;
  public isEdited = true;
  grossElectricityCost: number = 0;
  grossElectricityCostString: string = '';
  co2Emission: string = '';
  scenarioPlannerServiceSubscription: Subscription;
  scenarioPlannerServiceCloseSubscription: Subscription;
  lampSelectedSubcription: Subscription = new Subscription();
  scenarioPlannerSaveChanges = false;
  isMap: boolean = true;

  constructor(
    public authService: AuthService,
    public sidebarService: SidebarService,
    private appInsightsServiceDirective: AppInsightsServiceDirective,
    private emailService: EmailService,
    private energyMapService: EnergyMapService,
    private kamService: KamService,
    private lampsSelectedService: LampsSelectedService,
    private localNumberPipe: LocalNumberPipe,
    private mapHeaderControlsService: MapHeaderControlsService,
    private mapService: MapService,
    private mapStoreService: MapStoreService,
    private matDialog: MatDialog,
    private municipalityService: MunicipalityService,
    private notificationsService: NotificationsService,
    private overlayLoader: OverlayLoaderService,
    private router: Router,
    private scenarioPlannerService: ScenarioPlannerService,
    private streetlightsService: StreetlightApiService,
    private translate: TranslateService,
    private userMunicipalitiesService: UserMunicipalitiesService
  ) {
    super(sidebarService);
    this.notificationsService.destroy();
    this.scenarioPlannerServiceSubscription = this.energyMapService.scenarioPlannerSaveChanges$.subscribe((data) => {
      this.close();
      this.scenarioPlannerSaveChanges = data;
    });

    this.scenarioPlannerServiceCloseSubscription = this.energyMapService.scenarioPlannerClose$.subscribe((close) => {
      this.energyMapService.lampsRefreshingChanged(false);
      if (close) {
        if (!this.scenarioPlannerSaveChanges) {
          this.router.navigate(['energiekarte']);
        }

        this.reset();
      }
    });

    this.energyMapService.grossElectricityCostChanged$.pipe(takeUntil(this.unsubscribe$)).subscribe((price) => {
      this.grossElectricityCost = price;
      this.grossElectricityCostString = this.convertToGermanNumber((this.grossElectricityCost * 100).toFixed(1).toString());
      this.calculateCostSavings();
    });

    this.isKam = this.authService.isKAM;
  }

  public ngOnInit(): void {
    (this.mapStoreService.map as L.Map).closePopup();
    this.data.lampGroup = [];
    this.isAbleToSendToAnyEmail =
      this.isKam || this.authService.currentUser.roleCode === Roles.regionalStreetlightExpert || this.authService.currentUser.roleCode === Roles.poDSO;

    this.municipalityService
      .getByMunicipalityKey(this.userMunicipalitiesService.currentMunicipality.key)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((data) => {
        this.grossElectricityCost = data[0].grossElectricityPricePerUnit as number;
        this.grossElectricityCostString = this.convertToGermanNumber((this.grossElectricityCost * 100).toFixed(1).toString());
        if (!isNil(data[0].co2Emission)) {
          this.co2Emission = this.localNumberPipe.transform(data[0].co2Emission, 3);
        }
        this.calculateCostSavings();
      });

    this.mapService.selectMode(true);

    this.notificationsService.open(DescriptiveNotificationComponent, {
      model: {
        description:
          'Sie können neue Straßenbeleuchtung hinzufügen bzw. Empfehlungen abwählen indem Sie auf der Karte auf die jeweilige Straßenbeleuchtung klicken',
      },
    });

    let selectedLampInit = false;

    if (this.data.plannerScenarioId) {
      let savedLampsInit = false;
      const subscription = this.lampsSelectedService.lamps.subscribe((lamps) => {
        if (!savedLampsInit && lamps !== null && lamps.length > 0) {
          this.previouslySavedLamps = cloneDeep(lamps);
          savedLampsInit = true;
        } else {
          subscription.unsubscribe();
        }
      });
    }

    this.lampSelectedSubcription = this.lampsSelectedService.lamps.subscribe((lamps) => {
      const isSaved = !isNil(this.data.plannerScenarioId);

      if (this.data.recommendationType !== 'Manual' && !selectedLampInit) {
        this.updateLampClassByBatch(lamps);
        selectedLampInit = true;
      }
      this.data.lamps = cloneDeep(lamps);
      this.groupLamps();

      this.lampsCount = lamps.length;
      this.calculateCostSavings();
      this.isEdited = isNil(this.data.lamps) || this.checkForEdit() || !isSaved || this.hasNoReplacement();

      this.energyMapService.scenarioPlannerEdited(this.isEdited);
    });

    this.lightCategory = chain(this.data.lamps)
      .uniqBy('lightTypeName')
      .map((lamp: any) => lamp.lightType)
      .value();
  }

  updateLampClassByBatch(lamps: any) {
    this.energyMapService.lampsRefreshingChanged(true);
    const newLamps: any[] = differenceBy(cloneDeep(lamps), cloneDeep(this.data.lamps), 'assetId');
    let x = 0;
    const length = newLamps.length;

    const process = () => {
      for (; x < length; x++) {
        if (!this.energyMapService.lampsRefreshing.value) {
          this.lampsSelectedService.resetLamps();
          return;
        }

        const selectedLamp = find(this.mapStoreService.lampMarkers, (marker) => marker.options.data.equipment === newLamps[x].equipment);
        if (!isNil(selectedLamp)) {
          this.mapService.setClassLamp(selectedLamp, {
            isSelected: true,
            isHighlighted: selectedLamp.options.isHighlighted,
          });
        }

        if (x === length - 1) {
          this.energyMapService.lampsRefreshingChanged(false);
          return;
        }

        if (x !== 0 && x + 1 < length && x % 2 === 0) {
          setTimeout(process, 0);

          x += 1;
          return;
        }
      }
    };

    setTimeout(process, 0);
  }

  calculateCostSavings() {
    this.energySavings = this.roundWhole(this.sumAll('energySavings'), 10);
    this.costSavings = this.energySavings * this.grossElectricityCost;
    this.cO2Savings = this.computeCo2Savings();
    this.uvpCost = this.roundWhole(this.sumAll('uvpCost'), 10);

    if (this.isKam) {
      this.salesCost = this.roundWhole(this.sumAll('salesPrice'), 10);
    }
  }

  mapLampModel(group: any) {
    return chain(group.lamps)
      .groupBy((value) => value.currentModel + '|' + value.suggestedModel + '|' + (isNil(value.isRecommended) ? false : value.isRecommended))
      .toPairs()
      .map((item) => {
        return {
          id: item[0],
          currentModel: item[0].split('|')[0],
          suggestedModel: item[0].split('|')[1],
          lamps: item[1],
          isHighlighted: false,
        };
      })
      .value();
  }

  groupLampType() {
    return chain(this.data.lamps)
      .groupBy((value) => trim(capitalize(value.lightType)))
      .toPairs()
      .map((item) => zipObject(['lightType', 'lamps'], item))
      .value();
  }

  groupLamps() {
    let newLampGroup = [];

    const lampGroup = this.groupLampType();

    if (this.data.lampGroup.length > 0) {
      newLampGroup = differenceBy(lampGroup, this.data.lampGroup, 'lightType');
      pull(lampGroup, ...newLampGroup);

      forEach(lampGroup, (group: any) => {
        group.lamps = this.mapLampModel(group);
      });

      const intersectedLampGroup = intersectionBy(this.data.lampGroup, lampGroup, 'lightType');
      pullAllBy(this.data.lampGroup, differenceBy(this.data.lampGroup, intersectedLampGroup, 'lightType'), 'lightType');

      this.data.lampGroup.forEach((element: any) => {
        const editedGroup: any = find(lampGroup, (value) => value.lightType === element.lightType);
        if (!isNil(editedGroup)) {
          pullAllBy(element.lamps, differenceBy(element.lamps, editedGroup.lamps, 'id'), 'id');

          editedGroup.lamps.forEach((editedLampTransition: any) => {
            const oldLampTransition = find(element.lamps, (value: any) => value.id === editedLampTransition.id);

            if (!isNil(oldLampTransition)) {
              const intersectedLamps = intersectionBy(editedLampTransition.lamps, oldLampTransition.lamps, 'assetId');
              const newLamps = differenceBy(editedLampTransition.lamps, oldLampTransition.lamps, 'assetId');
              newLamps.forEach((lamp) => {
                this.updateLampClass(lamp, {
                  isSelected: true,
                  isHighlighted: isNil(oldLampTransition.isHighlighted) ? false : oldLampTransition.isHighlighted,
                });
              });

              oldLampTransition.lamps = [...intersectedLamps, ...newLamps];
            } else {
              element.lamps.push(editedLampTransition);
            }
          });

          element.lampsCount = this.getTotalLampsCount(element.lamps);
          element.typesCount = this.getModelsCountLabelByCategory(element.lamps);

          element.lamps.forEach((value: any) => {
            value.isHighlighted = this.isLampGroupHighlighted(value.lamps);
          });

          if (element.isHighlighted) {
            this.setHighlightGroup(editedGroup, element.isHighlighted);
          }
        }
      });

      if (newLampGroup.length > 0) {
        this.data.lampGroup.push(...this.initializeLampGroup(newLampGroup));
      }
    } else {
      this.data.lampGroup = this.initializeLampGroup(lampGroup);
    }

    forEach(this.data.lampGroup, (group) => {
      group.expanded = some(group.lamps, (a) => isNil(a.suggestedModel) || a.suggestedModel === 'null');
    });
  }

  initializeLampGroup(lampGroup: any[]) {
    forEach(lampGroup, (group: any) => {
      group.lamps = this.mapLampModel(group);
      group.lampsCount = this.getTotalLampsCount(group.lamps);
      group.typesCount = this.getModelsCountLabelByCategory(group.lamps);
      group.isHighlighted = false;
    });

    return lampGroup;
  }

  close() {
    if (this.isEdited && this.lampsCount > 0 && !!this.authService.permissions.STREETLIGHT_PLANNER) {
      this.matDialog
        .open(NotificationDialogComponent, {
          data: {
            title: this.translate.instant('module.modals.closeScenario.ourRecommendationHeaderText'),
            descriptionText: this.translate.instant('module.modals.closeScenario.doYouWantToSaveYourCreatedScenarioDescriptionText'),
            declineText: this.translate.instant('module.modals.closeScenario.discardScenarioFooterButtonText'),
            declineFaClass: 'fa-times-circle',
            confirmText: this.translate.instant('module.modals.closeScenario.saveScenarioFooterButtonText'),
            confirmFaClass: 'fa-check-circle',
            alignment: 'start',
            orientation: 'row',
          },
          width: '700px',
        })
        .afterClosed()
        .subscribe((res) => {
          if (res === true) {
            this.matDialog.closeAll();
            this.showSaveModal()
              .then(() => {
                this.energyMapService.scenarioPlannerClose(true);
              })
              .catch((warn) => {
                this.energyMapService.scenarioPlannerClose(false);
              });
          } else if (res === false) {
            this.energyMapService.scenarioPlannerClose(true);
            this.appInsightsServiceDirective.logEvent(ApplicationInsightEvent.streetlightPlannerCancel);
          } else {
            this.energyMapService.scenarioPlannerClose(false);
          }
        });
    } else {
      this.matDialog.closeAll();
      this.energyMapService.scenarioPlannerClose(true);
    }
  }

  scenarioPanelClose() {
    this.close();
  }

  reset() {
    this.mapService.selectMode(false);
    this.mapService.resetLampsIcon();
    this.lampsSelectedService.resetLamps();
    this.sidebarService.destroyComponent();
    this.notificationsService.destroy();
    this.energyMapService.scenarioPlannerEdited(false);
  }

  getModelsCountLabelByCategory(lamps: any) {
    if (lamps.length > 1) {
      return `${lamps.length} types`;
    }

    return `${lamps.length} type`;
  }

  getModelsByCategory(lampGroup: any[]) {
    return chain(lampGroup)
      .uniqBy('currentManufacturer')
      .map((lamp: any) => lamp.currentManufacturer)
      .value();
  }

  getTotalLampsCount(lampGroup: any[]) {
    let count = 0;

    forEach(lampGroup, (group: any) => {
      count += group.lamps.length;
    });

    return count;
  }

  sumAll(property: any) {
    let sum = 0;
    forEach(this.data.lampGroup, (lampCategory) => {
      forEach(lampCategory.lamps, (lampGroup) => {
        sum += this.sumBy(lampGroup.lamps, property);
      });
    });
    return sum;
  }

  sumBy(array: any[], property: any) {
    return sumBy(array, property);
  }

  roundWhole(value: number, digit: number) {
    return Math.round(value / digit) * digit;
  }

  toggleHighlightGroup(group: any) {
    group.isHighlighted = !group.isHighlighted;
    forEach(group.lamps, (x) => {
      x.isHighlighted = group.isHighlighted;
    });
    this.setHighlightGroup(group, group.isHighlighted);
  }

  setHighlightGroup(group: any[], value: any) {
    const lamps = this.getLampsInCategory(group);
    this.updateLampsClass(lamps, { isSelected: true, isHighlighted: value });
  }

  setHighlightedLamps(array: any, value: any) {
    array.lamps.forEach((element: any) => {
      this.updateLampClass(element, { isSelected: true, isHighlighted: value });
    });
  }

  updateLampClass(element: any, options: LampOptions) {
    const selectedLamp = find(this.mapStoreService.lampMarkers, (marker) => marker.options.data.equipment === element.equipment);
    if (!isNil(selectedLamp)) {
      if (selectedLamp.options.data.hasMultipleLamps === true) {
        selectedLamp.options.data.lamps.forEach((lamp: any) => {
          if (lamp.assetId === element.assetId) {
            lamp.isHighlighted = options.isHighlighted;
          }
        });

        const highlightedLamps = selectedLamp.options.data.lamps.find((lamp: any) => lamp.isHighlighted === true);
        let highlight = false;
        if (highlightedLamps) {
          highlight = true;
        }
        selectedLamp.options.isHighlighted = highlight;
        this.mapService.setClassLamp(selectedLamp, {
          isSelected: true,
          isHighlighted: highlight,
        });
      } else {
        this.mapService.setClassLamp(selectedLamp, options);
      }
    }
  }

  updateLampsClass(lamps: any[], options: LampOptions) {
    const lampMarkers: any[] = [];
    lamps.forEach((lamp) => {
      const selectedLamp = find(this.mapStoreService.lampMarkers, (marker) => marker.options.data.equipment === lamp.equipment);
      if (!isNil(selectedLamp)) {
        if (selectedLamp.options.data.hasMultipleLamps === true) {
          selectedLamp.options.data.lamps.forEach((l: any) => {
            if (l.assetId === lamp.assetId) {
              l.isHighlighted = options.isHighlighted;
            }
          });

          const highlightedLamps = selectedLamp.options.data.lamps.find((l: any) => l.isHighlighted === true);

          let highlight = false;
          if (highlightedLamps) {
            highlight = true;
          }
          selectedLamp.options.isHighlighted = highlight;
          this.mapService.setClassLamp(selectedLamp, {
            isSelected: true,
            isHighlighted: highlight,
          });
        }

        lampMarkers.push(selectedLamp);
      }
    });

    this.mapService.setClassLamp(lampMarkers, options);
  }

  removeByCategory(value: any) {
    this.overlayLoader.isLoading = true;

    setTimeout(() => {
      pull(this.data.lampGroup, value);

      const lamps: any[] = [];
      value.lamps.forEach((element: any) => {
        lamps.push(...element.lamps);
      });

      this.updateLampsClass(lamps, { isSelected: false, isHighlighted: false });

      this.lampsSelectedService.removeLamps(lamps);

      this.overlayLoader.isLoading = false;
    }, 300);
  }

  removeLampGroup(collection: any[], value: any) {
    this.overlayLoader.isLoading = true;
    setTimeout(() => {
      pull(collection, value);
      this.updateLampsClass(value.lamps, {
        isSelected: false,
        isHighlighted: false,
      });
      this.lampsSelectedService.removeLamps(value.lamps);
      this.overlayLoader.isLoading = false;
    }, 100);
  }

  showSaveModal() {
    return new Promise<void>((resolve, reject) => {
      if (!this.hasNoReplacement()) {
        this.saveScenario()
          .then((scenario) => {
            if (scenario) {
              this.data = Object.assign({}, this.data, scenario);
              this.previouslySavedLamps = cloneDeep(this.data.lamps);
              this.isEdited = false;
              this.energyMapService.scenarioPlannerEdited(this.isEdited);
              return resolve();
            }
          })
          .catch(() => reject());
      } else {
        return reject();
      }
    });
  }

  saveScenario() {
    const matRef = this.matDialog.open(SaveScenarioDialogComponent, {
      data: {
        plannerScenarioId: get(this.data, 'plannerScenarioId', null),
        name: get(this.data, 'name', null),
        scenarioTypeId: this.data.recommendationTypeId,
        scenarioType: this.data.recommendationType,
        municipalityGuid: this.userMunicipalitiesService.currentMunicipality.guid,
        municipalityKey: this.userMunicipalitiesService.currentMunicipality.key,
        budget: this.data.budget,
        scenarioLamp: map(this.getLamps(this.data.lampGroup), (lamp) => {
          return {
            lampAssetId: lamp.assetId,
            proposedReplacementProductId: lamp.productId,
            isRecommended: !isNil(lamp.isRecommended) ? lamp.isRecommended : false,
            preferredLuminosity: !isNil(lamp.preferredLuminosity) ? lamp.preferredLuminosity : '4000K',
          };
        }),
      },
      panelClass: ['-sm'],
    });

    return matRef.afterClosed().toPromise();
  }

  showProductCatalogByLampType(selectedGroup: any) {
    const lamps: any[] = [];

    forEach(selectedGroup.lamps, (lampGroup) => {
      lamps.push(...lampGroup.lamps);
    });

    this.selectedLampGroup = lamps;
    this.selectedLampGroupType = selectedGroup.lightType;
    this.selectedLampId = null;
    this.selectedLampIds = selectedGroup.lamps.map((lamp: any) => {
      return lamp.lamps[0].assetId;
    });
    this.selectedLampGroupCount = this.getTotalLampsCount(selectedGroup.lamps);
    this.productCatalog = true;
  }

  public showEmailModal(): void {
    if (this.isEdited) {
      this.matDialog.open(NotificationDialogComponent, {
        data: {
          descriptionText: 'Bitte speichern Sie Ihre Änderungen, bevor Sie mit dem Senden der E-Mail fortfahren',
          alignment: 'start',
          orientation: 'row',
        },
        panelClass: ['-md'],
      });
    } else {
      if (this.isAbleToSendToAnyEmail) {
        this.matDialog.open(EmailScenarioDialogComponent, {
          data: {
            id: this.data.plannerScenarioId,
            createdByName: this.data.createdByName,
            municipalityName: this.userMunicipalitiesService.currentMunicipality.name,
            budget: this.data.budget,
            scenarioType: this.data.recommendationType,
          },
          panelClass: ['-md'],
        });
      } else {
        this.kamService
          .getByMunicipalityIdDirect(this.userMunicipalitiesService.currentMunicipality.guid)
          .pipe(
            switchMap((kamContact: User | null) => {
              if (isNil(kamContact)) {
                return of(null);
              }

              const emailData = {
                Link: `${environment.energyPortalAppUrl}energiekarte/${this.data.plannerScenarioId}`,
                Municipality: this.userMunicipalitiesService.currentMunicipality.name,
                CreatedBy: this.authService.currentUser.firstname + ' ' + this.authService.currentUser.lastname,
              };

              const email = {
                subject: EmailContents.ScenarioPlannerCreated.subject,
                templateId: EmailContents.ScenarioPlannerCreated.templateId,
                recipient: kamContact.email,
                data: JSON.stringify(emailData),
              } as Email;

              return forkJoin([this.emailService.sendEmail(email), this.scenarioPlannerService.submit(this.data.plannerScenarioId)]);
            }),
            takeUntil(this.unsubscribe$)
          )
          .subscribe((data) => {
            if (!isNil(data)) {
              this.appInsightsServiceDirective.logEvent(ApplicationInsightEvent.streetlightPlannerSubmit, {
                scenarioType: this.data.recommendationType,
                budget: this.data.budget,
                municipalityName: this.userMunicipalitiesService.currentMunicipality.name,
              });

              this.matDialog.open(EmailScenarioDialogComponent, {
                data: {
                  isSent: true,
                  id: this.data.plannerScenarioId,
                  createdByName: this.data.createdByName,
                  municipalityName: this.userMunicipalitiesService.currentMunicipality.name,
                  budget: this.data.budget,
                  scenarioType: this.data.recommendationType,
                },
                panelClass: ['-md'],
              });
            }
          });
      }
    }
  }

  public getLamps(lampGroups: any[]) {
    const lamps: any[] = [];

    forEach(lampGroups, (lampCategory) => {
      lamps.push(...this.getLampsInCategory(lampCategory));
    });
    return lamps;
  }

  public getLampsInCategory(lampCategory: any) {
    const lamps: any[] = [];
    forEach(lampCategory.lamps, (lampGroup) => {
      lamps.push(...lampGroup.lamps);
    });
    return lamps;
  }

  public productSelected(products: any) {
    let lamps: any[] = [];

    lamps = map(this.selectedLampGroup, (element: any) => {
      return Object.assign(
        {},
        products.find((e: any) => e.suggestedModel),
        {
          assetId: element.assetId,
          equipment: element.equipment,
          currentManufacturer: element.currentManufacturer,
          currentModel: element.currentModel,
          age: element.age,
          preferredLuminosity: '4000K',
          bstNumber: element.bstNumber,
          isRecommended: false,
        }
      );
    });

    this.lampsSelectedService.updateLamps(lamps);
  }

  public showProductCatalogByModel(data: any) {
    if (data.lamps) {
      const assetId = data.lamps[0].assetId;

      this.selectedLampGroup = data.lamps;
      this.selectedLampIds = [assetId];
      this.selectedLampGroupType = data.lamps[0].lightType;
      this.selectedLampGroupCount = data.lamps.length;
      this.productCatalog = true;
    }
  }

  public closeProductCatalog(value: any) {
    this.productCatalog = value;
  }

  public hasNoReplacement() {
    return some(this.getLamps(this.data.lampGroup), (lamp) => isNil(lamp.suggestedModel));
  }

  public checkForEdit() {
    if (this.previouslySavedLamps.length !== this.data.lamps.length) {
      return true;
    }

    const lamps = differenceWith(this.data.lamps, this.previouslySavedLamps, (a: any, b: any) => {
      return isEqual(
        {
          assetId: a.assetId,
          suggestedModel: a.suggestedModel,
          preferredLuminosity: a.preferredLuminosity,
        },
        {
          assetId: b.assetId,
          suggestedModel: b.suggestedModel,
          preferredLuminosity: b.preferredLuminosity,
        }
      );
    });

    if (lamps.length > 0) {
      return true;
    }

    return false;
  }

  public isLampGroupHighlighted(lamps: any[]) {
    return lamps.some((lamp) => {
      const selectedLamp = find(this.mapStoreService.lampMarkers, (marker) => {
        return find(marker.options.data.lamps, (markerLamp) => markerLamp.assetId === lamp.assetId);
      });

      if (isNil(selectedLamp)) {
        return false;
      }

      return selectedLamp.options.isHighlighted;
    });
  }

  public onConversionHighlightChange(group: any, lampGroups: any[]) {
    group.isHighlighted = lampGroups.every((val) => val.isHighlighted === true);
  }

  public showProductDetailByModel(data: any) {
    this.lamp = data.lamps;
    this.selectedLampGroup = data.lamps;
    this.selectedLampGroupType = data.lamps[0].lightType;
    this.selectedLampIds = [data.lamps[0].assetId];
    this.selectedLampGroupCount = data.lamps.length;
    this.productDetail = true;
  }

  public closeDetail(value: any) {
    this.productDetail = value;
  }

  public setLampType(value: any) {
    this.selectedLampGroup = value;
    this.selectedLampIds = [value.lamps[0].assetId];
    this.selectedLampGroupType = value[0].lightType;
    this.selectedLampGroupCount = value.length;
  }

  public computeCo2Savings() {
    const sum = this.sumAll('cO2Savings');

    if (isNil(sum)) {
      return 0;
    } else {
      return sum / 1000;
    }
  }

  public ngOnDestroy(): void {
    this.notificationsService.destroy();
    this.matDialog.closeAll();
    this.lampsSelectedService.resetLamps();
    this.lampSelectedSubcription.unsubscribe();
    this.scenarioPlannerServiceSubscription.unsubscribe();
    this.scenarioPlannerServiceCloseSubscription.unsubscribe();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public trackByFn(_: number, item: any) {
    return item.lightType;
  }

  public trackByFn2(_: number, item: any) {
    return item.assetId;
  }

  clearLampHighlights() {
    this.data.lampGroup.forEach((lampGroup: any) => {
      this.onConversionHighlightChange(lampGroup, lampGroup.lamps);
      this.setHighlightGroup(lampGroup, false);
    });
  }

  openLocalSettings() {
    this.mapHeaderControlsService.openElectricityCostMenu();
  }

  convertToGermanNumber(value: string) {
    return value.replace('.', ',');
  }
  public checkIfManual(lampGroup: any) {
    return !lampGroup.lamps.every((lamp: any) => lamp.isRecommended === true);
  }

  public formatSuggestNewLampModel(suggestedModel: string) {
    if (suggestedModel === 'undefined' || suggestedModel === 'null' || isNil(suggestedModel)) {
      return 'Wähle ein Produkt';
    } else {
      return suggestedModel;
    }
  }

  public toggleStreetlightEfficiencyDialog(): void {
    if (this.authService.permissions.STREETLIGHT_EFFICIENCY) {
      const dialogRef = this.matDialog.openDialogs.filter((a) => a.id === 'streetlightEfficiencyDialog')[0];
      if (dialogRef) {
        dialogRef.close();
      } else {
        this.overlayLoader.isLoading = true;
        const filters = { includeNoCoordinates: true, includeNoCategory: true };
        const municipalityKey = this.userMunicipalitiesService.currentMunicipality.key;

        this.streetlightsService
          .getLuminairesByMunicipalityKey(municipalityKey, filters)
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe({
            next: (luminaires: Luminaire[]) => {
              forEach(this.data.lampGroup, (categoryGroup) => {
                forEach(categoryGroup.lamps, (lampGroup) => {
                  forEach(lampGroup.lamps, (lamp) => {
                    const luminaire = luminaires.filter((luminaire: Luminaire) => luminaire.assetId === lamp.assetId)[0];
                    if (!isNil(luminaire) && !isEmpty(luminaire) && !isNil(lamp.productId) && !isEmpty(lamp.productId)) {
                      luminaire.nettoLumen = lamp.suggestedLuminousFlux4000K || lamp.suggestedLuminousFlux3000K;
                      luminaire.sumPowerOfAllLuminaires = lamp.suggestedWatts;
                    }
                  });
                });
              });

              const newEfficiency = sumBy(luminaires, 'nettoLumen') / sumBy(luminaires, 'sumPowerOfAllLuminaires');

              this.overlayLoader.isLoading = false;

              this.matDialog.open(StreetlightEfficiencyDialogComponent, {
                data: {
                  isPlanner: true,
                  newEfficiency: newEfficiency,
                },
                width: '1000px',
                hasBackdrop: false,
                disableClose: true,
                panelClass: ['-md', 'streetlight-efficiency-dialog'],
                id: 'streetlightEfficiencyDialog',
              });
            },
            error: () => (this.overlayLoader.isLoading = false),
          });
      }
    }
  }
}
